// src/components/settings/email-template-form.jsx
import React, { useRef } from "react";
import { placeholdersFor } from "../../utils/emailPlaceholders";

const CHIP_CLASS = "inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-2 py-0.5 text-[11px] font-medium hover:bg-indigo-100 cursor-pointer";

function ChipRow({ templateId, onInsert }) {
  const groups = placeholdersFor(templateId);
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {groups.flatMap((g) => g.items).map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={() => onInsert(`{{${item.key}}}`)}
          className={CHIP_CLASS}
          title={item.display}
        >
          <span>{item.icon}</span><span>{item.display}</span>
        </button>
      ))}
    </div>
  );
}

export default function EmailTemplateForm({
  templateId,
  isRecap,
  subject, onSubjectChange,
  body, onBodyChange,
  intro, onIntroChange,
  closing, onClosingChange,
}) {
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const introRef = useRef(null);
  const closingRef = useRef(null);

  const insertInto = (ref, setter) => (text) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const before = el.value.slice(0, start);
    const after = el.value.slice(el.selectionEnd);
    const next = before + text + after;
    setter(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + text.length;
    });
  };

  return (
    <div className="p-5 bg-white border-r border-slate-200 overflow-y-auto">
      {/* Subject */}
      <div className="mb-4">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Subject</label>
        <ChipRow templateId={templateId} onInsert={insertInto(subjectRef, onSubjectChange)} />
        <input
          ref={subjectRef}
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
        />
      </div>

      {/* Body (non-recap) */}
      {!isRecap && (
        <div className="mb-4">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Body</label>
          <ChipRow templateId={templateId} onInsert={insertInto(bodyRef, onBodyChange)} />
          <textarea
            ref={bodyRef}
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            rows={14}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
          />
        </div>
      )}

      {/* Recap: intro + auto-built notice + closing */}
      {isRecap && (
        <>
          <div className="mb-4">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              Intro paragraph
              <span className="ml-1 font-normal text-slate-400 normal-case tracking-normal">— shown before the stat tiles</span>
            </label>
            <ChipRow templateId={templateId} onInsert={insertInto(introRef, onIntroChange)} />
            <textarea
              ref={introRef}
              value={intro}
              onChange={(e) => onIntroChange(e.target.value)}
              rows={6}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>
          <div className="mb-4 border border-dashed border-slate-300 rounded p-3 bg-slate-50">
            <div className="text-[11px] font-semibold text-slate-600 mb-1">⛓ Auto-built block (not editable)</div>
            <div className="text-[11px] text-slate-400 leading-relaxed">
              Stat tiles (Groups · 2nd Showings)<br/>
              + Hosted by · What agents said · What buyers said · Other comments
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              Closing paragraph
              <span className="ml-1 font-normal text-slate-400 normal-case tracking-normal">— shown below the sections, before the signature</span>
            </label>
            <ChipRow templateId={templateId} onInsert={insertInto(closingRef, onClosingChange)} />
            <textarea
              ref={closingRef}
              value={closing}
              onChange={(e) => onClosingChange(e.target.value)}
              rows={5}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>
        </>
      )}
    </div>
  );
}
